"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Metadata } from "next";
import Script from "next/script";
import { trackEvent } from "@/lib/mixpanel-client";
import { usePathname } from "next/navigation";

// Add metadata to the error page
export const metadata: Metadata = {
  title: "Error | Taberu",
  description: "An error occurred. Please try again.",
  robots: {
    index: false,
    follow: false,
  },
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    // Log the error to an error reporting service
    console.error("Global error occurred:", {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      pathname: pathname,
    });

    // Track the error with Mixpanel
    trackEvent("Client Error Displayed", {
      "Error Message": error.message || "Unknown error",
      "Error Stack Digest": error.digest || "No digest available",
      "Component Stack":
        error.stack?.split("\n").slice(0, 3).join(", ") || "No stack trace",
      "Page Path": pathname,
    });
  }, [error, pathname]);

  return (
    <html lang="en">
      <head>
        <title>Error | Taberu</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center max-w-md p-6">
            <h1 className="text-4xl font-bold text-primary mb-4">Oops!</h1>
            <h2 className="text-2xl font-semibold mb-4">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              We&apos;ve been notified and are working to fix the issue.
            </p>
            <Button
              onClick={() => {
                // Track error reset attempt
                trackEvent("Client Error Reset Attempted", {
                  "Error Message": error.message || "Unknown error",
                });
                reset();
              }}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Try again
            </Button>
          </div>
        </div>
        {/* Add structured data to indicate this is an error page */}
        <Script id="error-structured-data" type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://taberu.ai",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Error",
                item: "https://taberu.ai/error",
              },
            ],
          })}
        </Script>
      </body>
    </html>
  );
}
